/**
 * AWS WAF browser integration (TT-11461, scope 4 and 5).
 *
 * Fails OPEN on every path: an unset URL, a failed script load, or a missing `window.AwsWafIntegration`
 * all leave `wafFetch` on plain `window.fetch`. A WAF integration fault must not become a client outage.
 *
 * Loaded from JS rather than a `<script>` in `index.html` because CRA bakes an undefined
 * `%REACT_APP_%` placeholder into the HTML literally, and dev has no ACL/URL, so a static tag would
 * emit a broken `src`.
 */

// Each environment's per-ACL ApplicationIntegrationURL. Public, not a secret. The `challenge.js`
// entry point was verified serving 200 on the prod endpoint 2026-08-25. Empty until an environment
// has a blocklist ACL: preprod's lands with terraform-leveltix!404, dev has none.
const WAF_SDK_URL = process.env.REACT_APP_WAF_SDK_URL || '';

const CAPTCHA_CONTAINER_ID = 'waf-captcha-container';

const WAF_CAPTCHA_API_KEY = process.env.REACT_APP_WAF_CAPTCHA_API_KEY || '';

// Upper bound on how long wafFetch waits for the SDK before falling open to plain fetch. Bounds the
// worst case (slow/unreachable SDK host) so a WAF fault can never hang a request (TT-11556).
const WAF_SDK_READY_TIMEOUT_MS = 3000;

let sdkLoadPromise = null;

/**
 * Resolves false without injecting when no URL is set, and false rather than rejecting on load
 * error, so callers fall back to plain fetch.
 *
 * @return {Promise<boolean>} true only when the SDK is loaded and usable
 */
const loadWafSdk = () => {
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  if (!WAF_SDK_URL) {
    sdkLoadPromise = Promise.resolve(false);
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = WAF_SDK_URL;
    script.defer = true;
    script.onload = () => resolve(Boolean(window.AwsWafIntegration));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
};

/**
 * `AwsWafIntegration.fetch` attaches the `aws-waf-token` and solves a Challenge in-band when the SDK
 * is loaded; otherwise plain `fetch`, leaving the `apiClient` guard to handle any interstitial.
 *
 * Awaits the SDK before choosing the transport so an early call such as login does not race ahead
 * of the token (TT-11556). Fail-open: on timeout, load failure, or no URL it proceeds on plain `fetch`.
 *
 * @param {string|Request} input
 * @param {object} [init]
 * @return {Promise<Response>}
 */
const wafFetch = async (input, init) => {
  // clearTimeout in finally: when the SDK wins the race an uncleared timer would stay pending ~3s
  // per call and leak open handles in tests, so the timer must be cancelled once the race settles.
  let timerId;
  const timeout = new Promise((resolve) => {
    timerId = setTimeout(() => resolve(false), WAF_SDK_READY_TIMEOUT_MS);
  });
  try {
    await Promise.race([loadWafSdk(), timeout]);
  } finally {
    clearTimeout(timerId);
  }

  if (window.AwsWafIntegration && typeof window.AwsWafIntegration.fetch === 'function') {
    return window.AwsWafIntegration.fetch(input, init);
  }
  return window.fetch(input, init);
};

/**
 * NOT called anywhere today, by design (TT-11461 scope 5): the hook, not the activation. Inert until
 * an API key and the separate `captcha.js` SDK exist; returns false rather than throwing until then.
 *
 * @param {function(string): void} onSuccess called with the WAF token when the puzzle is solved
 * @param {function(Error): void} [onError]
 * @return {boolean} true only when a puzzle was actually rendered
 */
const renderWafCaptcha = (onSuccess, onError) => {
  const container = document.getElementById(CAPTCHA_CONTAINER_ID);
  if (!WAF_CAPTCHA_API_KEY || !container) {
    return false;
  }
  if (!window.AwsWafCaptcha || typeof window.AwsWafCaptcha.renderCaptcha !== 'function') {
    return false;
  }

  window.AwsWafCaptcha.renderCaptcha(container, {
    apiKey: WAF_CAPTCHA_API_KEY,
    onSuccess,
    onError,
    dynamicWidth: true,
  });
  return true;
};

export { loadWafSdk, wafFetch, renderWafCaptcha, CAPTCHA_CONTAINER_ID };
